import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export type SceneCtx = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  content: THREE.Group;
  dispose: () => void;
};

export function initScene(container: HTMLElement): SceneCtx {
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
    powerPreference: "low-power",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setSize(container.clientWidth, container.clientHeight, false);
  // Without this, mobile browsers steal touch gestures (pan/pinch-zoom the
  // page) before OrbitControls can rotate, making the viewer feel jittery.
  renderer.domElement.style.touchAction = "none";
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf2f2f0);

  // Guard against the container being measured at 0 on the very first effect
  // (NaN/Infinity aspect poisons every downstream camera math, including the
  // initial fit-to-content).
  const initW = Math.max(1, container.clientWidth);
  const initH = Math.max(1, container.clientHeight);
  const camera = new THREE.PerspectiveCamera(35, initW / initH, 0.5, 1000);
  camera.position.set(22, 16, -24);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(0, 1.4, 0);
  controls.enableDamping = true;
  controls.dampingFactor = 0.18;
  controls.rotateSpeed = 0.9;
  controls.zoomSpeed = 1.1;
  controls.minDistance = 5;
  controls.maxPolarAngle = Math.PI / 2.05;
  const computeMaxDistance = (aspect: number) =>
    aspect < 1 ? Math.min(500, 200 / aspect) : 200;
  controls.maxDistance = computeMaxDistance(camera.aspect);
  controls.update();

  scene.add(new THREE.AmbientLight(0xffffff, 0.65));
  const key = new THREE.DirectionalLight(0xffffff, 1.0);
  key.position.set(14, 24, 10);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.4);
  fill.position.set(-12, 8, -10);
  scene.add(fill);

  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(400, 400),
    new THREE.MeshStandardMaterial({
      color: 0xeae5d2,
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 8,
    }),
  );
  ground.rotation.x = -Math.PI / 2;
  // Keep the floor visually flush with the model, but bias it backward in the
  // depth buffer so it cannot shimmer through the deck top while orbiting.
  ground.position.y = -0.01;
  ground.renderOrder = -1;
  scene.add(ground);

  const content = new THREE.Group();
  // Mirror along world X so left/right in the 3D view match the 2D top-down
  // diagram. Without this, the camera's right vector points toward world -X
  // and CAD +X (screen-right in 2D) renders on the left of the 3D view.
  content.scale.x = -1;
  scene.add(content);

  let frame = 0;
  const tick = () => {
    controls.update();
    renderer.render(scene, camera);
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);

  const ro = new ResizeObserver(() => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    controls.maxDistance = computeMaxDistance(camera.aspect);
    renderer.setSize(w, h, false);
  });
  ro.observe(container);

  const dispose = () => {
    cancelAnimationFrame(frame);
    ro.disconnect();
    controls.dispose();
    disposeGroup(content);
    disposeGroup(scene);
    renderer.dispose();
    if (renderer.domElement.parentElement === container) {
      container.removeChild(renderer.domElement);
    }
  };

  return { renderer, scene, camera, controls, content, dispose };
}

export function disposeGroup(root: THREE.Object3D) {
  root.traverse((n) => {
    const mesh = n as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material;
    if (mat) {
      const list = Array.isArray(mat) ? mat : [mat];
      for (const m of list) {
        const anyMat = m as THREE.Material & { map?: THREE.Texture | null };
        if (anyMat.map) anyMat.map.dispose();
        m.dispose();
      }
    }
  });
}

export function clearGroup(group: THREE.Group) {
  while (group.children.length) {
    const child = group.children.pop()!;
    disposeGroup(child);
  }
}
